import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {PriceMutation, ProductRangeSliceModel} from "./productRangeSliceModel";
import {ProductModel, QuoteProductData,} from "../../../domain/models/productModel";
import axios, {AxiosError} from "axios";
import {CornyBakersEnv} from "../../../../../shared/environment/features/CornyBakersEnvironment";
import {ProductApiResponseModel} from "../../../domain/models/productApiResponseModel";
import i18next from "i18next";

import {RootState} from "../../../../../store";
import CategoryService from "../../../../header/infrastructure/services/categoryService";
import {Category} from "../../../../header/infrastructure/slices/menuSlice/menuSliceModel";
import {ImageModel} from "../../../domain/models/imageModel";

const API_BASE_URL = CornyBakersEnv.API_BASE_URL;

const isExplicitThtVariation = (variation: ProductApiResponseModel['variations'][number]) =>
    variation.cb_is_tht_action === true
    || variation.sku.toUpperCase().includes('-THT')
    || !!variation.cb_source_product?.id
    || !!variation.cb_source_product?.data?.id;

const getRegularVariation = (product: ProductApiResponseModel) => {
    if ((product.variations?.length ?? 0) === 0) {
        return null;
    }

    const articleNumber = product.cb_product?.article_number;
    if (articleNumber) {
        const articleVariation = product.variations.find(variation => variation.sku === articleNumber);
        if (articleVariation) {
            return articleVariation;
        }
    }

    return product.variations.find(variation => !isExplicitThtVariation(variation)) ?? product.variations[0];
};

const normalizeActionType = (type: unknown): 'none'|'regular'|'tht' => {
    if (type === 'regular' || type === 'tht') {
        return type;
    }
    return 'none';
};

const normalizeActionPrice = (value: unknown): number => {
    if (value === null || value === undefined || value === '') {
        return 0;
    }
    const normalized = typeof value === 'string' ? value.replace(',', '.') : value;
    const amount = Number(normalized);
    return Number.isFinite(amount) ? amount : 0;
};

const normalizeActionStock = (value: unknown): number => {
    const stock = Number(value ?? 0);
    return Number.isFinite(stock) ? stock : 0;
};

const formatThtDate = (value: unknown): string => {
    if (typeof value !== 'string' || value.length < 10) {
        return '';
    }

    const match = value.substring(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
        return '';
    }

    const [, year, month, day] = match;
    return `${day}-${month}-${year}`;
};

const getThtActionLabel = (date: unknown): string => {
    const formattedDate = formatThtDate(date);
    if (formattedDate) {
        return (i18next.t('Action THT @date', {ns: 'common'}) as string).replace('@date', formattedDate);
    }

    return i18next.t('THT action', {ns: 'common'}) as string;
};

const mapScaledPrices = (scaledPrices?: {price: string, quantity: number}[]) =>
    (scaledPrices ?? []).map(item => ({
        price: parseFloat(item.price),
        quantity: item.quantity,
    }));

export const formatProduct = (product: ProductApiResponseModel): ProductModel => {
    const regularVariation = getRegularVariation(product);
    const thtVariation = (product.variations ?? []).find(isExplicitThtVariation)
        ?? (product.variations ?? []).find(variation => variation.cb_action_type === 'tht' && variation.sku !== regularVariation?.sku)
        ?? null;
    const actionType = normalizeActionType(product?.cb_product?.cb_action_type ?? thtVariation?.cb_action_type ?? regularVariation?.cb_action_type);
    const actionPrice = normalizeActionPrice(
        product?.cb_product?.cb_action_price
        ?? thtVariation?.price?.number
        ?? regularVariation?.price?.number
    );
    const actionStock = normalizeActionStock(product?.cb_product?.cb_action_stock ?? thtVariation?.cb_tht_stock ?? 0);
    const actionActive = actionType === 'regular'
        ? actionPrice > 0
        : actionType === 'tht' ? (actionPrice > 0 && actionStock > 0) : false;
    const regularScaledPrices = (() => {
        const backend = mapScaledPrices(regularVariation?.scaled_prices);
        if (actionType === 'regular' && actionActive && actionPrice > 0) {
            return [{price: actionPrice, quantity: 0}];
        }
        return backend;
    })();
    const thtPrice = normalizeActionPrice(thtVariation?.price?.number ?? actionPrice);
    const thtScaledPrices = (() => {
        const backend = mapScaledPrices(thtVariation?.scaled_prices);
        if (backend.length > 0) {
            return backend;
        }
        return thtPrice > 0 ? [{price: thtPrice, quantity: 0}] : [];
    })();
    const thtStock = normalizeActionStock(thtVariation?.cb_tht_stock ?? actionStock);
    const thtAvailableStock = normalizeActionStock(thtVariation?.cb_tht_available_stock ?? thtStock);
    const thtEnabled = !!thtVariation && (thtVariation.status ?? true) && thtAvailableStock > 0;

    const variationOptions = [];
    if (regularVariation) {
        variationOptions.push({
            id: regularVariation.id,
            drupal_internal__variation_id: regularVariation.drupal_internal__variation_id,
            sku: regularVariation.sku,
            label: i18next.t('Regular product', {ns: 'common'}),
            disabled: false,
            is_limited_stock: false,
            max_quantity: undefined,
            scaled_prices: regularScaledPrices,
        });
    }
    if (thtVariation) {
        variationOptions.push({
            id: thtVariation.id,
            drupal_internal__variation_id: thtVariation.drupal_internal__variation_id,
            sku: thtVariation.sku,
            label: thtEnabled
                ? i18next.t('THT action (@quantity available)', {ns: 'common'}).replace('@quantity', `${thtAvailableStock}`)
                : i18next.t('THT action (sold out)', {ns: 'common'}),
            disabled: !thtEnabled,
            is_limited_stock: true,
            max_quantity: thtEnabled ? thtAvailableStock : 0,
            scaled_prices: thtScaledPrices,
        });
    }

    const defaultOption = (actionType === 'tht' && thtEnabled)
        ? variationOptions.find(option => option.id === thtVariation?.id)
        : variationOptions.find(option => option.id === regularVariation?.id);
    const selectedOption = defaultOption ?? variationOptions.find(option => !option.disabled) ?? variationOptions[0];
    const selectedVariation = selectedOption
        ? (product.variations ?? []).find(variation => variation.id === selectedOption.id)
        : (regularVariation ?? thtVariation);
    const actionLabel = actionActive
        ? (actionType === 'tht'
            ? getThtActionLabel(selectedVariation?.cb_tht_date ?? product?.cb_product?.cb_tht_date)
            : (selectedVariation?.cb_action_label || i18next.t('Action', {ns: 'common'}) as string))
        : '';
    const effectiveScaledPrices = selectedOption?.scaled_prices ?? [];

    return {
        id: product.id,
        cb_product_id: product.cb_product?.meta?.drupal_internal__target_id ?? null,
        drupal_internal__product_id: product.drupal_internal__product_id,
        name: product.title,
        sku: selectedVariation?.sku ?? '',
        description: product.body?.value,
        specifications: product?.product_specs?.value ?? '',
        quantity: 1,
        price: selectedVariation?.price.formatted ?? '€ 0.00',
        images: (product?.images?.length ?? 0) > 0 ? product.images.map((image): ImageModel => ({
            id: parseInt(image.id),
            name: image.meta.alt + " - " + image.meta.title,
            small_thumbnail: image.image_style_uri.small_thumbnail,
            thumbnail: image.image_style_uri.thumbnail,
            large: image.image_style_uri.large,
        })) : [],
        quote_product_data: product.quote_product_data?.map((quoteProductItem) => ({
            id: quoteProductItem.id ? parseInt(quoteProductItem.id) : null,
            label: quoteProductItem.label,
            url: quoteProductItem.url,
            productData: quoteProductItem.productData.map((productDataItem) => ({
                target_id: productDataItem.target_id ? parseInt(productDataItem.target_id) : null,
                product_price_target_id: productDataItem.product_price_target_id ? parseInt(productDataItem.product_price_target_id) : null,
                standard_cost: productDataItem.standard_cost ? parseFloat(productDataItem.standard_cost) : null,
                quote_price: productDataItem.quote_price ? parseFloat(productDataItem.quote_price) : null,
                quote_price_formatted: productDataItem.quote_price_formatted,
                quantity: productDataItem.quantity ? parseInt(productDataItem.quantity) : null,
                markup: productDataItem.markup ? parseFloat(productDataItem.markup) : null,
                remark: productDataItem.remark ? parseFloat(productDataItem.remark) : null,
                unit_label: productDataItem.unit_label,
            })),
        } as QuoteProductData)) ?? [],
        is_favorite: product.is_favorite ? {
            favorite: product.is_favorite.type !== undefined,
            type: product.is_favorite.type as unknown as string,
            id: product.is_favorite.id as unknown as string,
            data: product.is_favorite.data,
        } : null,
        variations: {
            drupal_internal__variation_id: selectedVariation?.drupal_internal__variation_id ?? 0,
            id: selectedVariation?.id ?? '',
            product_id: {
                id: selectedVariation?.product_id.id ?? 0,
            }
        },
        variation_options: variationOptions,
        scaled_prices: effectiveScaledPrices,
        action_type: actionType,
        action_label: actionLabel,
        action_active: actionActive,
    } as ProductModel;
};

export const getProducts = createAsyncThunk(
    "productRangeSlice/getProducts",
    async (filter: { fullText: string, is_favorite: boolean }, thunkAPI) => {
        const state: RootState = thunkAPI.getState() as RootState;
        const page = state.productRange.page - 1;

        const lang = i18next.language;
        const formatLang = lang === 'nl' ? '' : `/${lang}`;

        const offset = page === 0 ? 0 : state.productRange.itemsPerPage * page;
        let url = `${API_BASE_URL}${formatLang}/jsonapi/index/products/?jsonapi_include=1&include=images,variations,cb_product&filter[entity_type]=commerce_product&sort[artnr][path]=sku&sort[artnr][direction]=ASC&page[offset]=${offset}&page[limit]=${state.productRange.itemsPerPage}&page[u]=${state.currentUser.currentUser?.relation ?? ''}`;

        if (filter.fullText) {
            url += "&filter[fulltext]=" + filter.fullText;
        }

        if (filter.is_favorite && state?.currentUser?.currentUser?.relation) {
            url += `&filter[favorites]=${state.currentUser.currentUser.relation}`;
        } else if (filter.is_favorite) {
            return thunkAPI.rejectWithValue('Something went wrong, please try again');
        }

        const categoryService = new CategoryService([] as Category[]);
        const currentCategoryFilter = categoryService.getCurrentCategoryFilterId();
        if (currentCategoryFilter) {
            url += `&filter[categories]=${currentCategoryFilter}`;
        }

        try {
            const response = await axios.get(url, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const productsResponse = response.data;

            if (productsResponse.data.length === 0) {
                return thunkAPI.rejectWithValue('No products found');
            }

            if (productsResponse.data.length === 0 && !filter.fullText) {
                return thunkAPI.rejectWithValue('No results were found for');
            }

            const ids = (productsResponse.data as ProductApiResponseModel[]).map(p => p.cb_product.meta.drupal_internal__target_id);
            const latestRequestId = (thunkAPI.getState() as RootState).productRange.currentProductsRequestId;
            if (latestRequestId === thunkAPI.requestId) {
                thunkAPI.dispatch(getUpcomingPriceMutations({prodIds: ids}));
            }

            return {
                products: productsResponse.data.map(formatProduct),
                totalItems: productsResponse.meta.count,
            };

        } catch (error: unknown) {
            if (error instanceof AxiosError) {
                return thunkAPI.rejectWithValue(error.message);
            }
            return thunkAPI.rejectWithValue('Something went wrong, please try again');
        }
    });


export const getUpcomingPriceMutations = createAsyncThunk(
    "productRangeSlice/getUpcomingPriceMutations",
    async ({prodIds}: {prodIds: number[]}, thunkAPI) => {

        try {
            const lang = i18next.language;
            const formatLang = lang === 'nl' ? '' : `/${lang}`;

            const response = await axios.post(`${API_BASE_URL}${formatLang}/api/upcoming-price-mutations`, {
                products: prodIds
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            return response.data;

        } catch (error: unknown) {
            if (error instanceof AxiosError) {
                return thunkAPI.rejectWithValue(error.message);
            }
            return thunkAPI.rejectWithValue('Something went wrong, please try again');
        }
    });

const initialState = {
    loading: false,
    productRange: [] as ProductModel[],
    totalItems: 0,
    page: 1,
    itemsPerPage: 15,
    currentProductsRequestId: null,

    upcoming_price_mutations: [],
    upcoming_price_mutations_loading: false,
    upcoming_price_mutations_loaded: false,
} as ProductRangeSliceModel;

const productRangeSlice = createSlice({
    name: "productRangeSlice",
    initialState,
    reducers: {
        updateRange: (state, action: PayloadAction<ProductModel[]>) => {
            state.productRange = action.payload;
            if (action.payload.length < state.itemsPerPage) {
                state.totalItems = action.payload.length;
            }
        },
        setItemsPerPage: (state, action) => {
            state.itemsPerPage = action.payload as number;
        },
        setPage: (state, action) => {
            state.page = action.payload as number;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getProducts.pending, (state, action) => {
                state.loading = true;
                state.currentProductsRequestId = action.meta.requestId;
            })
            .addCase(getProducts.rejected, (state, action) => {
                if (state.currentProductsRequestId !== action.meta.requestId) {
                    return;
                }
                state.loading = false;
                state.currentProductsRequestId = null;
                state.productRange = [];
                state.totalItems = 0;
            })
            .addCase(getProducts.fulfilled, (state, action) => {
                if (state.currentProductsRequestId !== action.meta.requestId) {
                    return;
                }
                state.loading = false;
                state.currentProductsRequestId = null;
                state.productRange = action.payload.products as ProductModel[];
                state.totalItems = action.payload.totalItems;
            })
            .addCase(getUpcomingPriceMutations.pending, (state) => {
                state.upcoming_price_mutations_loading = true;
            })
            .addCase(getUpcomingPriceMutations.rejected, (state) => {
                state.upcoming_price_mutations_loading = false;
                state.upcoming_price_mutations_loaded = true;
            })
            .addCase(getUpcomingPriceMutations.fulfilled, (state, action) => {
                state.upcoming_price_mutations_loading = false;
                state.upcoming_price_mutations_loaded = true;
                state.upcoming_price_mutations = action.payload as PriceMutation[];
            })
    }
});

export const {updateRange, setItemsPerPage, setPage} = productRangeSlice.actions;
export default productRangeSlice.reducer;
